import { useEffect, useState, useRef } from "react";
import { Camera, Trash2, Car } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { resizeImage } from "../lib/img";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const STATUS_OPTIONS = [
  ["tersedia", "Tersedia"],
  ["digunakan", "Digunakan"],
  ["penyelenggaraan", "Penyelenggaraan"],
  ["dilupus", "Dilupus"],
];

const EMPTY = { registration_number: "", vehicle_type: "Sedan", make: "", model: "", fuel_type: "Petrol", category: "Sedan", current_odometer: 0, status: "tersedia", unit_id: "", geotab_id: "" };

export function VehicleFormDialog({ open, onOpenChange, vehicle, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [photoThumb, setPhotoThumb] = useState(null);
  const [photoDirty, setPhotoDirty] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data));
    api.get("/vehicle-categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(() => {
    if (open) {
      setForm(vehicle ? { ...EMPTY, ...vehicle } : EMPTY);
      setPhoto(vehicle?.photo || null);
      setPhotoDirty(false);
      if (vehicle?.id && !vehicle.photo) {
        api.get(`/vehicles/${vehicle.id}`).then((r) => {
          if (r.data?.vehicle?.photo) setPhoto((p) => p || r.data.vehicle.photo);
        }).catch(() => {});
      }
    }
  }, [open, vehicle]);

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPhoto(await resizeImage(file, 700));
      setPhotoThumb(await resizeImage(file, 160, 0.72));
      setPhotoDirty(true);
    } catch {
      toast.error("Gagal memproses imej");
    }
    e.target.value = "";
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoThumb(null);
    setPhotoDirty(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      let vid = vehicle?.id;
      if (vehicle) {
        await api.put(`/vehicles/${vehicle.id}`, { ...form, current_odometer: Number(form.current_odometer) });
      } else {
        const { data } = await api.post("/vehicles", { ...form, current_odometer: Number(form.current_odometer) });
        vid = data.id;
      }
      if (photoDirty && vid) {
        await api.put(`/vehicles/${vid}/photo`, { photo: photo || "", photo_thumb: photoThumb });
      }
      toast.success(vehicle ? "Kenderaan dikemas kini" : "Kenderaan didaftarkan");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{vehicle ? "Kemas Kini Kenderaan" : "Daftar Kenderaan"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5 col-span-2">
            <Label>Foto Kenderaan</Label>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="vehicle-form-photo-input" />
            <div className="flex items-center gap-3">
              {photo ? (
                <img src={photo} alt="Kenderaan" className="h-20 w-32 rounded-lg object-cover border shadow-sm" data-testid="vehicle-form-photo-preview" />
              ) : (
                <span className="h-20 w-32 rounded-lg bg-slate-100 border flex items-center justify-center text-slate-400">
                  <Car className="h-8 w-8" />
                </span>
              )}
              <div className="space-y-2">
                <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} data-testid="vehicle-form-photo-btn">
                  <Camera className="h-3.5 w-3.5 mr-1.5" /> {photo ? "Tukar Foto" : "Muat Naik Foto"}
                </Button>
                {photo && (
                  <Button type="button" variant="outline" size="sm" className="flex w-full whitespace-nowrap text-[#DC3545] border-[#DC3545] hover:bg-red-50" onClick={removePhoto} data-testid="vehicle-form-photo-remove-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5 shrink-0" /> Buang Foto
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Jika tiada foto, imej kategori akan digunakan secara automatik.</p>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>No. Pendaftaran</Label>
            <Input data-testid="vehicle-reg-input" required value={form.registration_number}
              onChange={(e) => setForm({ ...form, registration_number: e.target.value })} placeholder="WXY 1234" />
          </div>
          <div className="space-y-1.5">
            <Label>Jenama</Label>
            <Input data-testid="vehicle-make-input" required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Proton" />
          </div>
          <div className="space-y-1.5">
            <Label>Model</Label>
            <Input data-testid="vehicle-model-input" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Persona" />
          </div>
          <div className="space-y-1.5">
            <Label>Jenis</Label>
            <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
              <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["Sedan", "SUV", "Van", "MPV", "Pikap", "Lori", "Motosikal"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Bahan Api</Label>
            <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
              <SelectTrigger data-testid="vehicle-fuel-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["Petrol", "Diesel", "Hibrid", "Elektrik"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="vehicle-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-56">
                {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Odometer Semasa (KM)</Label>
            <Input data-testid="vehicle-odometer-input" type="number" min="0" required value={form.current_odometer}
              onChange={(e) => setForm({ ...form, current_odometer: e.target.value })} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Bahagian / Unit</Label>
            <Select value={form.unit_id || ""} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
              <SelectTrigger data-testid="vehicle-unit-select"><SelectValue placeholder="Pilih Bahagian/Unit" /></SelectTrigger>
              <SelectContent className="bg-white max-h-56">
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Geotab ID (Telematik)</Label>
            <Input data-testid="vehicle-geotab-input" value={form.geotab_id || ""}
              onChange={(e) => setForm({ ...form, geotab_id: e.target.value })} placeholder="Cth: b1A2C3" />
            <p className="text-xs text-muted-foreground">ID peranti Geotab untuk padanan data telematik kenderaan ini.</p>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {STATUS_OPTIONS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="col-span-2 bg-[#10056E] hover:bg-[#2a17a8]" data-testid="vehicle-save-btn">
            {vehicle ? "Kemas Kini" : "Daftar"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
